import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Mail, MessageSquare, Send, User } from "lucide-react";
import { toast } from "sonner";
import { SectionHead, Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";
import { useSEO } from "@/lib/seo";

export default function ContactPage() {
  useSEO("Contact the Shop Desk — The Mechanic's Tool Kit", "Questions about the kits, licensing, or updates? Message the shop desk — a human who knows the documents answers within one business day.");
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resendBusy, setResendBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received. We'll get back to you within one business day.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send. Try again.");
    } finally {
      setSending(false);
    }
  };

  const fieldCls = "w-full bg-raised border border-slate-400/15 px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-blaze/60 transition-colors";

  return (
    <main className="pt-28 pb-24 min-h-screen blueprint-grid" data-testid="contact-page">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <SectionHead
          overline="Support // The Shop Desk"
          title="Talk to a human who knows the paperwork"
          sub="Pre-sale questions, licensing, bulk/shop-group pricing, or a template that needs a tweak — message us here."
        />
        <div className="mt-14 grid lg:grid-cols-12 gap-8">
          <Reveal className="lg:col-span-7">
            <form onSubmit={submit} className="clip-notch border border-slate-400/15 bg-panel p-8 space-y-5">
              <div>
                <label htmlFor="contact-name" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-2">Your name</label>
                <input id="contact-name" data-testid="contact-name-input" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={fieldCls} placeholder="Sam Torque" />
              </div>
              <div>
                <label htmlFor="contact-email" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-2">Email</label>
                <input id="contact-email" data-testid="contact-email-input" required type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={fieldCls} placeholder="sam@yourshop.com" />
              </div>
              <div>
                <label htmlFor="contact-message" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-2">Message</label>
                <textarea id="contact-message" data-testid="contact-message-input" required rows={6} value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className={`${fieldCls} resize-none`} placeholder="Tell us about your shop and what you need…" />
              </div>
              <button data-testid="contact-submit-button" disabled={sending}
                className="inline-flex items-center gap-2.5 bg-blaze text-white font-display font-bold uppercase tracking-wide text-base px-8 py-4 clip-notch-sm transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_0_25px_rgba(249,115,22,0.4)] disabled:opacity-60">
                {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                Send it over
              </button>
            </form>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={0.12}>
            <div className="border border-slate-400/10 bg-panel p-8 h-full">
              <h3 className="font-display text-xl font-bold uppercase tracking-tight text-slate-50">Direct lines</h3>
              <ul className="mt-6 space-y-5 text-sm">
                <li className="flex gap-3"><Mail className="w-5 h-5 text-blaze shrink-0" />
                  <div><p className="text-slate-200">support@mechanicstoolkit.com</p>
                  <p className="text-slate-500 text-xs mt-1">Replies within one business day</p></div></li>
                <li className="flex gap-3"><MessageSquare className="w-5 h-5 text-blaze shrink-0" />
                  <div><p className="text-slate-200">Download or access issue?</p>
                  <p className="text-slate-500 text-xs mt-1">Include your purchase email — we re-issue links fast</p></div></li>
                <li className="flex gap-3"><User className="w-5 h-5 text-blaze shrink-0" />
                  <div><p className="text-slate-200">Shop groups & fleets</p>
                  <p className="text-slate-500 text-xs mt-1">Multi-location licensing available on request</p></div></li>
              </ul>
              <div className="mt-8 pt-6 border-t border-slate-400/10">
                <Link to="/admin" data-testid="admin-view-trigger" className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-blaze transition-colors">
                  Shop owner? Admin login →
                </Link>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="mt-8 clip-notch border border-slate-400/15 bg-panel p-8">
            <h3 className="font-display text-xl font-bold uppercase tracking-tight text-slate-50">Lost your download links?</h3>
            <p className="mt-2 text-sm text-slate-400 leading-relaxed">
              Bought a kit and can't find the confirmation page or the receipt email? Enter the email you
              used at checkout — if there's a paid order on it, fresh download links go out within a minute.
            </p>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!resendEmail) return;
                setResendBusy(true);
                try {
                  await api.post("/purchases/resend", { email: resendEmail });
                  toast.success("If a paid order exists for that email, fresh download links are on their way.");
                  setResendEmail("");
                } catch {
                  toast.error("Could not process that right now — try again in a minute.");
                } finally {
                  setResendBusy(false);
                }
              }}
              className="mt-5 flex flex-col sm:flex-row gap-3"
            >
              <input
                data-testid="resend-email-input"
                type="email"
                required
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                placeholder="Email used at checkout"
                className="flex-1 bg-raised border border-slate-400/15 px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-blaze/60 transition-colors"
              />
              <button
                data-testid="resend-submit-button"
                disabled={resendBusy}
                className="inline-flex items-center justify-center gap-2 bg-blaze text-white font-display font-bold uppercase tracking-wide text-sm px-6 py-3.5 clip-notch-sm transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_0_25px_rgba(249,115,22,0.4)] disabled:opacity-60"
              >
                {resendBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Resend my links"}
              </button>
            </form>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
